import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

interface ChangeRequest {
  id: string;
  garageId: string;
  garageName: string;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
}

export default function GarageChangeRequestsPage() {
  const auth = useAuth();
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  // Spec 069 — `?status=` preselects the filter (dashboard tile); absent keeps Pending.
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<string>(searchParams.get('status') ?? 'PENDING');

  useEffect(() => {
    if (auth.token) loadRequests();
  }, [filter, auth.token]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const url = filter ? `/api/garages/change-requests?status=${filter}` : '/api/garages/change-requests';
      const data = await api.get<ChangeRequest[]>(url, auth.token!);
      setRequests(data || []);
    } catch (err) {
      console.error('Failed to load change requests:', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e' }}>Garage Change Requests</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
        >
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : requests.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No change requests found.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: '#6b7280', fontSize: '0.8125rem' }}>Garage</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: '#6b7280', fontSize: '0.8125rem' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: '#6b7280', fontSize: '0.8125rem' }}>Submitted</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: '#6b7280', fontSize: '0.8125rem' }}>Reviewed</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: '#6b7280', fontSize: '0.8125rem' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.75rem', fontWeight: 500 }}>{req.garageName || req.garageId}</td>
                <td style={{ padding: '0.75rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: req.status === 'PENDING' ? '#fef3c7' : req.status === 'APPROVED' ? '#d1fae5' : '#fee2e2',
                    color: req.status === 'PENDING' ? '#92400e' : req.status === 'APPROVED' ? '#065f46' : '#991b1b',
                  }}>{req.status}</span>
                </td>
                <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>{new Date(req.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>{req.reviewedAt ? new Date(req.reviewedAt).toLocaleDateString() : '-'}</td>
                <td style={{ padding: '0.75rem' }}>
                  <Link to={`/change-requests/${req.id}`} style={{ color: '#030c54', fontWeight: 500 }}>Review</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
